// src/pages/Invoices/InvoiceHistoryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import {
  Eye,
  Loader2,
  RefreshCw,
  Search,
  FileDown,
} from "lucide-react";
import { toast } from "react-hot-toast";
import OperationalSummaryCards from "@/components/Invoices/OperationalSummaryCards";

const currency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value || 0));

function statusClass(status) {
  const safe = String(status || "").toLowerCase();

  if (safe === "paid") return "text-green-300 bg-green-500/10 border-green-400/30";
  if (safe === "approved") return "text-blue-300 bg-blue-500/10 border-blue-400/30";
  if (safe === "rejected") return "text-red-300 bg-red-500/10 border-red-400/30";
  if (safe.includes("pending")) return "text-amber-300 bg-amber-500/10 border-amber-400/30";

  return "text-gray-300 bg-white/5 border-white/10";
}

function exportCsv(rows) {
  const headers = [
    "invoice_id",
    "invoice_number",
    "vendor_name",
    "po_number",
    "status",
    "amount_due",
    "total_paid",
    "remaining",
    "uploaded_at",
    "approved_at",
    "paid_at",
  ];

  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((key) => {
          const value = row?.[key] ?? "";
          return `"${String(value).replaceAll('"', '""')}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `invoice-history-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();

  URL.revokeObjectURL(url);
}

const InvoiceHistoryPage = () => {
  const navigate = useNavigate();
  const {
    isAdmin,
    isDeveloper,
    hasPermission,
  } = useUserPermissions();

  const canView =
    Boolean(
      isAdmin ||
      isDeveloper ||
      hasPermission(
        "invoice.invoice.view"
      )
    );

  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    if (!canView) navigate("/forbidden");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const fetchHistory = async () => {
    setLoading(true);

    try {
      const res = await api.get(
        "/solo-mdsh-api/invoice/history"
      );

      const payload = res.data || {};
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.invoices)
          ? payload.invoices
          : Array.isArray(payload.history)
            ? payload.history
            : Array.isArray(payload.rows)
              ? payload.rows
              : [];

      setHistory(rows);
      setSummary(payload.summary || null);
    } catch (error) {
      console.error("❌ Error fetching invoice history:", error);
      if (error?.response?.status === 403) return navigate("/forbidden");
      toast.error("Failed to load invoice history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();

    return history.filter((inv) => {
      const invStatus = String(inv.status || inv.payment_status || "").toLowerCase();

      if (status !== "all" && invStatus !== status) return false;
      if (!q) return true;

      return [
        inv.invoice_id,
        inv.invoice_number,
        inv.vendor_name,
        inv.po_number,
        inv.status,
        inv.payment_status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [history, search, status]);

  const localSummary = useMemo(() => {
    const totalValue = filteredHistory.reduce(
      (sum, inv) => sum + Number(inv.amount_due || inv.amount || 0),
      0
    );

    const paidValue = filteredHistory.reduce(
      (sum, inv) => sum + Number(inv.total_paid || 0),
      0
    );

    return {
      count: filteredHistory.length,
      totalValue,
      paidValue,
      openValue: Math.max(totalValue - paidValue, 0),
    };
  }, [filteredHistory]);

  const handleView = (invoiceId) => {
    navigate(`/invoice/detail/${invoiceId}`);
  };

  const handleReview = (invoiceId) => {
    navigate(`/invoice/review/${invoiceId}`);
  };

  if (!canView) return null;

  return (
    <div className="min-h-screen p-6 space-y-6 bg-background text-white">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">🧠 Operational Intelligence</h1>
          <p className="text-sm text-gray-400 mt-1">
            Full invoice audit history across uploaded, approved, paid, rejected, and system-generated invoices.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={fetchHistory}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => exportCsv(filteredHistory)}
            disabled={!filteredHistory.length}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold hover:bg-blue-500 disabled:opacity-50"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <OperationalSummaryCards summary={summary} localSummary={localSummary} />

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice, vendor, PO, status..."
            className="w-full bg-[#101010] border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-400"
          />
        </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-[#101010] border border-white/15 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="unpaid">Unpaid</option>
          <option value="pending approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-blue-300">
          <Loader2 className="animate-spin" />
          Loading invoice history...
        </div>
      ) : filteredHistory.length === 0 ? (
        <p className="text-yellow-400">No invoices found.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-neutral-800 text-gray-200">
              <tr>
                <th className="p-3 border-b border-white/10 text-left">Invoice</th>
                <th className="p-3 border-b border-white/10 text-left">Vendor</th>
                <th className="p-3 border-b border-white/10 text-left">PO</th>
                <th className="p-3 border-b border-white/10 text-right">Amount</th>
                <th className="p-3 border-b border-white/10 text-right">Paid</th>
                <th className="p-3 border-b border-white/10 text-right">Remaining</th>
                <th className="p-3 border-b border-white/10 text-left">Status</th>
                <th className="p-3 border-b border-white/10 text-left">Last Date</th>
                <th className="p-3 border-b border-white/10 text-right">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredHistory.map((inv, idx) => {
                const safeStatus = inv.status || inv.payment_status || "unknown";
                const amount = Number(inv.amount_due || inv.amount || 0);
                const paid = Number(inv.total_paid || 0);
                const remaining =
                  inv.remaining != null ? Number(inv.remaining) : Math.max(amount - paid, 0);

                const lastDate =
                  inv.paid_at ||
                  inv.approved_at ||
                  inv.rejected_at ||
                  inv.uploaded_at ||
                  inv.finalized_date;

                return (
                  <tr
                    key={inv.invoice_id}
                    className={idx % 2 === 0 ? "bg-neutral-900" : "bg-neutral-800"}
                  >
                    <td className="p-3 border-b border-white/10">
                      <div className="font-mono text-amber-300">
                        {inv.invoice_number || `#${inv.invoice_id}`}
                      </div>
                      <div className="text-xs text-gray-400">ID {inv.invoice_id}</div>
                    </td>

                    <td className="p-3 border-b border-white/10">
                      {inv.vendor_name || "Unknown vendor"}
                    </td>

                    <td className="p-3 border-b border-white/10">
                      {inv.po_number || inv.po_id || "—"}
                    </td>

                    <td className="p-3 border-b border-white/10 text-right">
                      {currency(amount)}
                    </td>

                    <td className="p-3 border-b border-white/10 text-right text-green-300">
                      {currency(paid)}
                    </td>

                    <td className="p-3 border-b border-white/10 text-right text-amber-300">
                      {currency(remaining)}
                    </td>

                    <td className="p-3 border-b border-white/10">
                      <span
                        className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(
                          safeStatus
                        )}`}
                      >
                        {String(safeStatus).toUpperCase()}
                      </span>
                    </td>

                    <td className="p-3 border-b border-white/10">
                      {lastDate ? new Date(lastDate).toLocaleString() : "—"}
                    </td>

                    <td className="p-3 border-b border-white/10 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleView(inv.invoice_id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded inline-flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Detail
                        </button>

                        <button
                          onClick={() => handleReview(inv.invoice_id)}
                          className="border border-white/15 hover:bg-white/10 text-white px-3 py-1 rounded inline-flex items-center gap-1"
                        >
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceHistoryPage;
